/**
 * registration-beleg.ts - the proof («Beleg») a broker picks for a manual «Bestätigen» or «Ablehnen»
 * of a registration and for lifting a policy's registration exception (P5-S5 review rounds 1 and 2):
 * the registration's mails, each mail's stored attachments and the customer's documents. A mail is sent
 * as communicationId, an attachment or a document as documentId; the api checks that it belongs to the
 * customer or the registration (beleg_not_found).
 *
 * The mails and their attachments come from GET /registrations/:regId/beleg-options: the customer's
 * mails and the mails exchanged with the insurer (also those filed only on the insurer), never an unsent
 * draft, newest first, paged by next_cursor («Weitere laden»). The documents come from the Dokumente tab
 * hook of the customer.
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import type { RegistrationBelegOptionsDto } from '@brokly/shared';
import { apiClient } from '../../lib/api-client';
import { dateTimeShortDe } from '../../lib/format';
import { qk } from '../../lib/query-keys';
import { useClientDocumentRows } from '../documents/hooks/useClientDocumentRows';

/** How many mails one page of beleg-options asks for. */
export const BELEG_PAGE_SIZE = 25;

export type BelegKind = 'mail' | 'attachment' | 'document';

export interface BelegOption {
  /** The communication id (mail) or the document id (attachment, document). */
  id: string;
  kind: BelegKind;
  label: string;
}

/** The body fields of a picked Beleg. */
export function belegBody(option: BelegOption | undefined): { communicationId?: string; documentId?: string } {
  if (!option) return {};
  return option.kind === 'mail' ? { communicationId: option.id } : { documentId: option.id };
}

type BelegMail = RegistrationBelegOptionsDto['items'][number];

/** «Eingang 22.10.2026, 09:00: AW: Brokermandat (meldungen@axa.ch)». */
export function belegMailLabel(m: BelegMail): string {
  const dir = m.direction === 'inbound' ? 'Eingang' : 'Ausgang';
  const when = dateTimeShortDe(m.received_at);
  const subject = m.subject?.trim() ? m.subject : 'E-Mail ohne Betreff';
  return `${dir}${when ? ` ${when}` : ''}: ${subject}${m.from ? ` (${m.from})` : ''}`;
}

export interface RegistrationBelege {
  /** The mails in the api order (newest first), each followed by its stored attachments. */
  mails: BelegOption[];
  /** The customer's documents (without those already listed as an attachment). */
  documents: BelegOption[];
  byId: Map<string, BelegOption>;
  /** The api has a further page of mails. */
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => void;
}

/**
 * The Beleg options of a registration: its mails (each followed by its stored attachments), then the
 * customer's documents. Without a registration only the documents.
 */
export function useRegistrationBelege(registrationId: string | null | undefined, clientId: string): RegistrationBelege {
  const documents = useClientDocumentRows(clientId);
  const pages = useInfiniteQuery<RegistrationBelegOptionsDto>({
    queryKey: qk.registrationBelegOptions(registrationId ?? ''),
    queryFn: ({ pageParam }) =>
      apiClient.registrations.belegOptions(registrationId ?? '', {
        limit: BELEG_PAGE_SIZE,
        ...(typeof pageParam === 'string' && { cursor: pageParam }),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: Boolean(registrationId),
  });

  const options: BelegOption[] = [];
  const seen = new Set<string>();
  const push = (o: BelegOption) => {
    if (seen.has(o.id)) return;
    seen.add(o.id);
    options.push(o);
  };
  for (const page of pages.data?.pages ?? []) {
    for (const m of page.items) {
      push({ id: m.communication_id, kind: 'mail', label: belegMailLabel(m) });
      for (const a of m.attachments) push({ id: a.document_id, kind: 'attachment', label: `Anhang: ${a.filename}` });
    }
  }
  const mailCount = options.length;
  for (const d of documents.rows) {
    push({ id: d.id, kind: 'document', label: `${d.title}${d.dateDisplay ? ` (${d.dateDisplay})` : ''}` });
  }
  return {
    mails: options.slice(0, mailCount),
    documents: options.slice(mailCount),
    byId: new Map(options.map((o) => [o.id, o])),
    hasMore: Boolean(registrationId) && pages.hasNextPage,
    loadingMore: pages.isFetchingNextPage,
    loadMore: () => {
      if (pages.hasNextPage && !pages.isFetchingNextPage) void pages.fetchNextPage();
    },
  };
}

/** The picked Beleg as body fields (a mail as communicationId, else documentId). */
export function pickedBeleg(belege: RegistrationBelege, id: string): { communicationId?: string; documentId?: string } {
  return belegBody(id === '' ? undefined : belege.byId.get(id));
}
